import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useCart, PRODUCTS } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import "./ProductDetail.css";
import jarHara from "../assets/jar-hara.webp";
import jarTurmeric from "../assets/jar-turmeric.webp";
import jarHoney from "../assets/jar-honey.webp";
import haraNameakLifestyle from "../assets/hara-namak-lifestyle.webp";
import haraNameakPairing from "../assets/hara-namak-pairing.png";

const IMAGES = {
  "hara-namak": jarHara,
  turmeric: jarTurmeric,
  honey: jarHoney,
  "hemp-salt": jarHara,
  "flax-salt": jarHara,
};

const PRODUCT_DETAILS = {
  "hara-namak": {
    tagline: "The green salt of Pahadi kitchens",
    origin: "Kumaon Hills, Uttarakhand",
    benefits: ["No preservatives", "No artificial colour", "Hand-ground", "Rich in minerals"],
    story: "The green salt of Pahadi kitchens — Pisi Lun — has been ground by hand in Kumaoni homes for generations. Rock salt, green chilies, mountain herbs. No preservatives. No artificial colour. Just the taste every Pahadi grew up with, and the purity every customer deserves.",
    usage: "Sprinkle over fresh salads and cut fruits for an instant flavour boost. Stir into curd or buttermilk for a Pahadi raita. Add to idli batter before steaming. Mix into fresh fruit chaat. Shake over popcorn, roasted makhana or any snack that needs a spiced salt kick. Squeeze lemon over anything with Hara Namak — citrus unlocks its full flavour.",
    ingredients: "Rock salt, green chili, mountain herbs",
    pairings: ["Fresh Salad", "Cut Fruits", "Curd", "Buttermilk", "Idli", "Fruit Chaat", "Popcorn", "Makhana", "Raita"],
    lifestyleImage: haraNameakLifestyle,
    pairingImage: haraNameakPairing,
    didYouKnow: "Pisi Lun has been a staple of Kumaoni kitchens for over 200 years. The word 'Pisi' means 'ground' in Kumaoni — this salt was traditionally ground by hand on stone chakki every morning. The green colour comes from mountain herbs naturally rich in chlorophyll — not any artificial colour.",
  },
  turmeric: {
    tagline: "Golden turmeric from 6,000 ft farms",
    origin: "High-altitude farms, Uttarakhand",
    benefits: ["Anti-inflammatory", "No polish or additives", "Hand-ground", "Wild-grown"],
   story: "Grown above 6,000 feet where cold nights force the plant to produce more curcumin. Wild, unpolished, hand-ground. Not the bright yellow powder you find in supermarkets — this is turmeric the way it was always meant to be.",
    usage: "Add to milk, curries, rice, or face packs. Use 1/4 tsp daily for wellness. Add a pinch of black pepper to increase absorption by 2000%.",
    ingredients: "Pure wild turmeric powder",
    pairings: ["Warm Milk", "Curries", "Rice", "Smoothies", "Face Pack", "Kahwa", "Scrambled Eggs", "Kadha", "Soup"],
    didYouKnow: "Most commercial turmeric is polished with chemicals to look bright yellow. Ours is unpolished, wild-grown above 6,000 feet. Our turmeric has 3-5% curcumin vs 1-2% in regular market turmeric.",
  },
  honey: {
    tagline: "Raw honey from wild Himalayan beehives",
    origin: "Wild beehives above 6,000 ft",
    benefits: ["Raw & unfiltered", "Unheated", "No additives", "Wild forest flora"],
    story: "Harvested from wild beehives in Himalayan forests. Never heated. Never filtered. Every enzyme, every mineral, every drop of wild forest flora — intact. Raw honey the way nature made it.",
    usage: "Have a spoon daily, drizzle on toast, mix in warm (not hot) water, or use as a natural sweetener. Never heat it — warmth above 40°C destroys enzymes.",
    ingredients: "100% raw wild mountain honey",
    pairings: ["Warm Water", "Toast", "Green Tea", "Kahwa", "Yoghurt", "Pancakes", "Oats", "Lemon Drink", "Roti with Ghee"],
    didYouKnow: "Wild mountain honey crystallises faster than commercial honey — and that's a sign of purity, not spoilage. Commercial honey doesn't crystallise because it's been heated and filtered, destroying all its natural properties.",
  },
  "hemp-salt": {
    tagline: "Protein-rich table companion",
    origin: "Himalayan mineral belt",
    benefits: ["Rich in Omega-3", "Cold-pressed hemp", "No additives", "High protein"],
    story: "Cold-pressed hemp seeds blended with Himalayan mineral salt. One of the most complete plant proteins in nature — nine essential amino acids, Omega-3, minerals. A Pahadi superfood, now on your table.",
    usage: "Sprinkle on salads, dal, rice or yoghurt. Mix into atta while making roti. Blend into smoothies for a protein boost.",
    ingredients: "Cold-pressed hemp seeds, Himalayan mineral salt",
    pairings: ["Salads", "Curd", "Smoothies", "Roti Atta", "Rice", "Poha", "Upma", "Grain Bowls", "Yoghurt"],
    didYouKnow: "Hemp seeds contain all 9 essential amino acids — making them one of the very few plant-based complete proteins. Per gram, they have more protein than chicken. They've been part of Pahadi diets for hundreds of years.",
  },
  "flax-salt": {
    tagline: "Nutty, earthy, everyday",
    origin: "Mountain mills, Uttarakhand",
    benefits: ["High in fibre", "Rich in Omega-3", "Roasted for flavour", "No additives"],
    story: "Roasted flax seeds, ground with mountain mineral salt. Rich in Omega-3, fibre and lignans. We roast before grinding — it unlocks a deep nutty aroma that raw flax simply doesn't have. Health without compromise.",
    usage: "Mix in curd, sprinkle on roti, add to smoothies or use as a table seasoning. Add to dal before serving for a nutty crunch.",
    ingredients: "Roasted flax seeds, mountain mineral salt",
    pairings: ["Curd", "Roti", "Smoothies", "Dal", "Khichdi", "Rice with Ghee", "Raita", "Salads", "Lassi"],
    didYouKnow: "Flax seeds must be ground to be absorbed by the body — whole seeds pass through undigested. That's why we roast and grind them before blending with mountain salt. The roasting also unlocks a deep, nutty aroma that raw flax seeds don't have.",
  },
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

export default function ProductDetail() {
  const { id } = useParams();
  const { addToCart, cart } = useCart();
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();

  const product = PRODUCTS[id];
  const details = PRODUCT_DETAILS[id];

  const [added, setAdded] = useState(false);
  const [pincode, setPincode] = useState("");
  const [pincodeInput, setPincodeInput] = useState("");
  const [edd, setEdd] = useState(null);
  const [eddLoading, setEddLoading] = useState(false);
  const [cityName, setCityName] = useState("");
  const [welcomeEligible, setWelcomeEligible] = useState(false);

  const cartQty = cart.find(i => i.id === id)?.quantity || 0;

  useEffect(() => {
    if (userProfile?.addresses?.length > 0) {
      const firstAddr = userProfile.addresses[0];
      if (firstAddr.pincode && firstAddr.pincode.length === 6) {
        setPincode(firstAddr.pincode);
        setPincodeInput(firstAddr.pincode);
      }
    }
  }, [userProfile]);

  useEffect(() => {
    if (!pincode || pincode.length !== 6) return;
    setEddLoading(true);
    setCityName("");
    Promise.all([
      fetch(`${BACKEND_URL}/api/edd?pincode=${pincode}&weight=${(product?.weight || 200) / 1000}`).then(r => r.json()),
      fetch(`${BACKEND_URL}/api/pincode/${pincode}`).then(r => r.json()),
    ])
      .then(([eddData, pincodeData]) => {
        setEdd(eddData);
        if (pincodeData.valid) setCityName(pincodeData.area || pincodeData.city);
        setEddLoading(false);
      })
      .catch(() => { setEdd({ label: "4–7 business days" }); setEddLoading(false); });
  }, [pincode, product]);

  useEffect(() => {
    if (!currentUser || !product) return;
    if (product.id === "honey") return;
    async function check() {
      try {
        const res = await fetch(`${BACKEND_URL}/api/validate-coupon`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            code: "WELCOME149",
            email: currentUser.email || "",
            phone: userProfile?.phone || "",
            cartItems: [{ id: product.id }],
          }),
        });
        const data = await res.json();
        setWelcomeEligible(data.valid);
      } catch (e) {}
    }
    check();
  }, [currentUser, userProfile, product]);

  function handleAdd() {
    addToCart(id);
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  }

  function handleCheckPincode() {
    const clean = pincodeInput.replace(/\D/g, "").slice(0, 6);
    if (clean.length === 6) setPincode(clean);
  }

  if (!product || !details) {
    return (
      <div className="pd-not-found">
        <h2>Product not found</h2>
        <Link to="/products">← Back to Products</Link>
      </div>
    );
  }

  const relatedProducts = Object.values(PRODUCTS).filter(p => p.id !== id && p.id !== "test-1").slice(0, 3);

  return (
    <div className="pd-page">
      <div className="pd-breadcrumb page-container">
        <Link to="/products">Products</Link> / <span>{product.name}</span>
      </div>

      <div className="pd-hero page-container">
        <div className="pd-image-wrap">
          <div className="pd-image-bg">
            <img src={IMAGES[id]} alt={product.name} className="pd-image" />
          </div>
          {welcomeEligible && (
            <div className="pd-welcome-badge">Use WELCOME149 — get this for ₹149</div>
          )}
        </div>

        <div className="pd-info">
          <span className="pd-origin">📍 {details.origin}</span>
          <h1 className="pd-name">{product.name}</h1>
          <p className="pd-tagline">{details.tagline}</p>

          <div className="pd-price-row">
            <span className="pd-price">₹{product.price}</span>
            <span className="pd-weight">{product.weight}g</span>
          </div>

          <div className="pd-benefits">
            {details.benefits.map((b, i) => (
              <span key={i} className="pd-benefit">✓ {b}</span>
            ))}
          </div>

          <div className="pd-edd-wrap">
            <div className="pd-edd-row">
              <input
                type="tel"
                placeholder="Enter pincode"
                value={pincodeInput}
                onChange={e => setPincodeInput(e.target.value.replace(/\D/g, "").slice(0, 6))}
                onKeyDown={e => e.key === "Enter" && handleCheckPincode()}
                className="pd-edd-input"
                maxLength={6}
              />
              <button onClick={handleCheckPincode} className="pd-edd-btn">Check</button>
            </div>
            {eddLoading && <p className="pd-edd-result">Checking delivery...</p>}
            {edd && !eddLoading && (
              <p className="pd-edd-result">
                {cityName && <span className="pd-city-name">Delivering to {cityName} — </span>}
                <strong>{edd.label}</strong>{edd.courier && ` via ${edd.courier}`}
              </p>
            )}
          </div>

          <button className={`pd-add-btn ${added ? "added" : ""}`} onClick={handleAdd}>
            {added ? `✓ ${cartQty} in Cart` : "Add to Cart"}
          </button>
          {cartQty > 0 && (
            <Link to="/cart" className="pd-view-cart">View Cart →</Link>
          )}
          <p className="pd-ingredients"><strong>Ingredients:</strong> {details.ingredients}</p>
        </div>
      </div>

      {/* Story section */}
      <div className="pd-story page-container">
        <div className="pd-story-inner">
          <h2>The Story</h2>
          <p>{details.story}</p>
          <div className="pd-usage">
            <h3>How to use</h3>
            <p>{details.usage}</p>
          </div>
          {details.didYouKnow && (
            <div className="pd-did-you-know">
              <h3>Did you know?</h3>
              <p>{details.didYouKnow}</p>
            </div>
          )}
          <div className="pd-usage" style={{ marginTop: 28 }}>
            <h3>Best paired with</h3>
            {details.pairingImage ? (
              <div className="pd-pairing-diagram">
                <div style={{ position: "relative", display: "inline-block", width: "100%" }}>
                  <img src={details.pairingImage} alt="Best paired with" style={{ width: "100%", borderRadius: 16 }} />
                  <svg viewBox="0 0 1600 900" style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }} xmlns="http://www.w3.org/2000/svg">
                    <text x="148" y="248" textAnchor="middle" fontFamily="Playfair Display, serif" fontSize="28" fontWeight="600" fill="#2D5016">Fresh Salad</text>
                    <text x="420" y="115" textAnchor="middle" fontFamily="Playfair Display, serif" fontSize="28" fontWeight="600" fill="#2D5016">Cut Fruits</text>
                    <text x="780" y="95" textAnchor="middle" fontFamily="Playfair Display, serif" fontSize="28" fontWeight="600" fill="#2D5016">Curd</text>
                    <text x="1100" y="115" textAnchor="middle" fontFamily="Playfair Display, serif" fontSize="28" fontWeight="600" fill="#2D5016">Buttermilk</text>
                    <text x="1380" y="280" textAnchor="middle" fontFamily="Playfair Display, serif" fontSize="28" fontWeight="600" fill="#2D5016">Makhana</text>
                    <text x="1350" y="530" textAnchor="middle" fontFamily="Playfair Display, serif" fontSize="28" fontWeight="600" fill="#2D5016">Raita</text>
                    <text x="980" y="780" textAnchor="middle" fontFamily="Playfair Display, serif" fontSize="28" fontWeight="600" fill="#2D5016">Popcorn</text>
                    <text x="700" y="820" textAnchor="middle" fontFamily="Playfair Display, serif" fontSize="28" fontWeight="600" fill="#2D5016">Fruit Chaat</text>
                    <text x="340" y="700" textAnchor="middle" fontFamily="Playfair Display, serif" fontSize="28" fontWeight="600" fill="#2D5016">Idli</text>
                  </svg>
                </div>
              </div>
            ) : (
              <div className="pd-usage-tags">
                {(details.pairings || []).map((p, i) => (
                  <span key={i} className="pd-usage-tag">{p}</span>
                ))}
              </div>
            )}
          </div>
        </div>
        {details.lifestyleImage && (
          <div className="pd-lifestyle-wrap">
            <img src={details.lifestyleImage} alt={product.name + " lifestyle"} className="pd-lifestyle-img" />
          </div>
        )}
      </div>

      {/* Related products */}
      <div className="pd-related page-container">
        <h2>You may also like</h2>
        <div className="pd-related-grid">
          {relatedProducts.map(p => (
            <div key={p.id} className="pd-related-card" onClick={() => navigate(`/products/${p.id}`)}>
              <img src={IMAGES[p.id]} alt={p.name} />
              <div className="pd-related-info">
                <h4>{p.name}</h4>
                <span>₹{p.price}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}